import * as React from "react";
import {
    Box, Stack, Typography, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Checkbox
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Localized, useLocalization } from "@fluent/react";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { ModuleSelect } from "../components/ModuleSelect";
import type { ComputerizedSystemModule as Module } from "../computerizedSystemsApi";

export const ProcessEditDialog: React.FC<{
    open: boolean;
    title: string;
    initial: { name: string; description?: string | null; moduleId?: number | null };
    modules: Module[];
    systemLevelModuleId: number;
    onCancel: () => void;
    onSave: (data: { name: string; description: string | null; moduleId: number | null }) => void;
}> = ({ open, title, initial, modules, systemLevelModuleId, onCancel, onSave }) => {
    const [name, setName] = React.useState(initial.name);
    const [desc, setDesc] = React.useState(initial.description ?? "");
    const [moduleId, setModuleId] = React.useState<number | null>(initial.moduleId ?? null);
    const [withoutModule, setWithoutModule] = React.useState(false);
    const [touched, setTouched] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const RequiredError = () => <Localized id="field-is-required"><span>_Pole jest wymagane</span></Localized>

    const { l10n } = useLocalization()

    React.useEffect(() => {
        if (open) {
            setName(initial.name);
            setDesc(initial.description ?? "");
            const isWithoutModule = !initial.moduleId || initial.moduleId === -1;
            setWithoutModule(isWithoutModule);
            setModuleId(isWithoutModule ? systemLevelModuleId : initial.moduleId ?? null);
            setTouched(false);
            setSaving(false);
        }
    }, [open, initial, systemLevelModuleId]);

    const nameErr = touched && name.trim() === "";
    const handleSave = async () => {
        setTouched(true);
        if (!name.trim()) return;
        if (!withoutModule && !moduleId) return;
        
        setSaving(true);
        try {
            await onSave({ 
                name: name.trim(), 
                description: desc.trim() || null, 
                moduleId: withoutModule ? null : moduleId 
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCheckboxChange = (checked: boolean) => {
        setWithoutModule(checked);
        if (checked) {
            setModuleId(systemLevelModuleId);
        } else {
            setModuleId(null);
        }
    };

    const selectedModuleName = moduleId && moduleId !== systemLevelModuleId 
        ? modules.find(m => m.id === moduleId)?.name 
        : null;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle 
                    sx={{
                        bgcolor: (theme) => theme.palette.grey[200],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.75, 
                    }}>
                {title}
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2} padding={2}>
                    {(moduleId || withoutModule) && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.25 }} />
                            <Typography variant="body2" color="text.secondary">
                                {withoutModule || moduleId === systemLevelModuleId
                                    ? l10n.getString("cs-process-info-system-level", null, "_This process is at system level (without module)")
                                    : l10n.getString("cs-process-info-module", { moduleName: selectedModuleName || "" }, "_This process belongs to module: {moduleName}")}
                            </Typography>
                        </Box>
                    )}
                    <ModuleSelect
                        modules={modules}
                        value={moduleId}
                        onChange={(value) => setModuleId(value)}
                        disabled={saving || withoutModule}
                        excludeSystemLevel={true}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={withoutModule}
                                onChange={(e) => handleCheckboxChange(e.target.checked)}
                                disabled={saving}
                            />
                        }
                        label={l10n.getString("cs-process-without-module", null, "_Process without module")}
                    />
                    {!withoutModule && !moduleId && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: -1 }}>
                            <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.25 }} />
                            <Typography variant="body2" color="text.secondary">
                                {l10n.getString("cs-process-module-required", null, "_Please select a module or check 'Process without module' to continue.")}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        label={l10n.getString("name", null, "_Nazwa")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        error={nameErr}
                        helperText={nameErr ? <RequiredError /> : undefined}
                        fullWidth
                        disabled={saving}
                    />
                    <TextField
                        label={l10n.getString("cs-optional-description", null, "_Opis (opcjonalnie)")}
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={saving}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <CancelButton loading={saving} onClick={onCancel} />
                <SaveButton loading={saving} onClick={handleSave} disabled={!withoutModule && !moduleId} />
            </DialogActions>
        </Dialog>
    );
};
